/**
 * @module app.MainController
 */
import appBase from './index.js';
import {DIRECT_LINKS, HOST_TO_LAND, LANDS, LAND_ALIASES, LAND_LAYER_TO_PSEUDO_GROUP_LAYER, LAND_TO_DOMAIN_NAME, LAND_TO_LAYER, LAND_TO_LAYER_TYPE, LAYERGROUPS, LAYER_REPLACEMENT, LAYER_TREE, MAIN_MENU_EVENT_NAME, MAP_PROJECTION, MENU_TOGGLE_COLLAPSE, MOUNTAINEERING_LAYERS, MVT_LAYERS, ORDERED_LANDS, VECTOR_LAYERS, VECTOR_TILE_LAYERS, WMTS_GROUPS, Z_INDEX} from './constants.js';
import {schweizmobil, swisstopo} from './sources.js';
import './maintenance/maintenanceComponent.js';
import {extentMerge, getConstrainedResolution, isWinterLayer, layerIndex, numberWithSpaces, objectFilter, objectForEach, onRendered, poiStyle, smallScreen, visibleSubMenu} from './utils.js';
import {getBrowserLanguage} from 'ngeo/utils';
import {proj as proj2056} from '@geoblocks/proj/src/EPSG_2056.js';
import olMap from 'ol/Map.js';
import olView from 'ol/View.js';
import {remove as olArrayRemove} from 'ol/array';
import olControlMousePosition from 'ol/control/MousePosition.js';
import olControlScaleLine from 'ol/control/ScaleLine.js';
import olControlZoom from 'ol/control/Zoom.js';
import {listen, unlistenByKey} from 'ol/events';
import {extendCoordinate, getArea, getCenter} from 'ol/extent.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olGeomCircle from 'ol/geom/Circle.js';
import {defaults as olInteractionDefaults} from 'ol/interaction.js';
import olLayerTile from 'ol/layer/Tile.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import {appendParams} from 'ol/uri';
import {isEmpty} from "ol/obj";
import {createEmpty} from "ol/extent";
import {createRouteLayer, updateRouteLayerStyle, createRouteSource} from './route/layer.js';
import {createSlowupVectorLayer, createSlowupVectorSource} from './slowup/slowupLayer.js';
import {createSwissparkVectorLayer, createSwissparkVectorSource} from "./swisspark/swissparkLayer.js";
import {CrosscountryNetworkLayer} from "./crosscountry.js";


// Pointer Events Polyfill for safari < 13 support
import 'elm-pep';

// Promise.allSettled polyfill for safari < 13 support (used by @geoblocks/print)
import 'allsettled-polyfill';

import olFeature from "ol/Feature";
import olPoint from "ol/geom/Point";

import './AuthenticationService.js';
import './filter.js';
import './CacheBustService.js';
import './CesiumLoader.js';
import './EmbedService.js';
import './FeatureConverter.js';
import './HighlightFeature.js';
import './GeolocationService.js';
import './MapService.js';
import './MobileInteractionService.js';
import './RouteLogoService.js';
import './PhotoService.js';
import './detour/Service.js';
import './RasterSynchronizer.js';
import './cast.js';
import './checkboxDirective.js';
import './collapseDirective.js';
import './datepickerDirective.js';
import './geolocationComponent.js';
import './hideDirective.js';
import './onImageLoadDirective.js';
import './printDirective.js';
import './PrintHelper.js';
import './QueryController.js';
import './searchDirective.js';
import './timeoutClassDirective.js';
import './tooltipDirective.js';
import './track/edit.js';
import './track/facts.js';
import './track/list.js';
import './track/poi.js';
import './track/poieditmobile.js';
import './track/mobiledrawing.js';
import './track/filterlist.js';
import './track/filters.js';
import './user/welcome.js';
import './validFileDirective.js';
import 'ngeo/misc/controlComponent.js';
import 'ngeo/map/component.js';
import './controls3dComponent';
import './UserController.js';


/**
 * @private
 * @param {?Array<number>} c
 * @return {string}
 */
function coordinateFormat(c) {
  return c ? `${numberWithSpaces(c[0])}, ${numberWithSpaces(c[1])}` : "";
}

/**
 * @param {angular.Scope} $scope Scope.
 * @param {angularGettext.Catalog} gettextCatalog Gettext catalog.
 * @param {string} langUrlTemplate Language URL template.
 * @param {ngeo.statemanager.Location} ngeoLocation ngeo Location service.
 * @param {angular.$filter} $filter Filter service.
 * @param {angular.$timeout} $timeout Timeout service.
 * @param {app.HighlightFeature} appHighlightFeature
 * @param {app.GeolocationService} appGeolocationService
 * @param {app.MapService} appMapService
 * @param {angular.$http} $http
 * @param {string} apiUrl
 * @param {string} wmtsUrls
 * @param {string} swisstopoWmtsUrls
 * @param {string} poiLayersDataUrl
 * @param {string} baseDataUrl
 * @param {appx.SourceConfig} sourceConfig
 * @param {app.CesiumLoader} appCesiumLoader
 * @param {Array.<number>} resolutions
 * @param {app.RouteLogoService} appRouteLogoService
 * @param {app.detour.Service} appDetourService
 * @param {app.AuthenticationService} appAuthenticationService
 * @param {app.PhotoService} appPhotoService
 * @param {app.EmbedService} appEmbedService
 * @param {ui.router.$state} $state
 * @param {app.MobileInteractionService} appMobileInteractionService
 * @param {!app.CacheBustService} appCacheBustService
 * @param {!app.query.state.Service} appQueryStateService
 * @param {app.waiting} appWaiting
 * @struct
 * @constructor
 * @ngInject
 * @export
 */
export default class MainController {
  constructor(
    $scope,
    gettextCatalog,
    langUrlTemplate,
    ngeoLocation,
    $filter,
    $timeout,
    appHighlightFeature,
    appGeolocationService,
    appMapService,
    $http,
    apiUrl,
    wmtsUrls,
    swisstopoWmtsUrls,
    poiLayersDataUrl,
    baseDataUrl,
    sourceConfig,
    appCesiumLoader,
    resolutions,
    appRouteLogoService,
    appDetourService,
    appAuthenticationService,
    appPhotoService,
    appEmbedService,
    $state,
    appMobileInteractionService,
    appCacheBustService,
    appQueryStateService,
    appWaiting
  ) {

    $scope.$root['mainCtrl'] = this;

    /**
     * @export
     */
    this.cesium = appCesiumLoader;

    /**
     * @private
     */
    this.appRouteLogoService_ = appRouteLogoService;

    /**
     * @private
     */
    this.appDetourService_ = appDetourService;

    /**
     * @private
     */
    this.authentication_ = appAuthenticationService;

    /**
     * @private
     */
    this.appPhotoService_ = appPhotoService;

    /**
     * @private
     */
    this.http_ = $http;

    /**
     * @type {string}
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @type {string}
     * @private
     */
    this.wmtsUrls_ = wmtsUrls;

    /**
     * @type {string}
     * @private
     */
    this.swisstopoWmtsUrls_ = swisstopoWmtsUrls;

    /**
     * @type {string}
     * @private
     */
    this.poiLayersDataUrl_ = poiLayersDataUrl;

    /**
     * @type {string}
     * @private
     */
    this.baseDataUrl_ = baseDataUrl;

    /**
     * @private
     */
    this.routingState_ = $state;

    /**
     * @private
     */
    this.appWaiting_ = appWaiting;

    /**
     * @type {boolean}
     * @export
     */
    this.slowup = false;

    /**
     * @type {Object.<string, ol.layer.Tile>}
     * @private
     */
    this.adminLayers_ = null;

    /**
     * @type {appx.SourceConfig}
     */
    this.sourceConfig = sourceConfig;

    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    /**
     * @type {angular.$timeout}
     * @private
     */
    this.timeout_ = $timeout;

    /**
     * @type {angularGettext.Catalog}
     */
    this.gettextCatalog = gettextCatalog;

    /**
     * @type {string}
     * @private
     */
    this.langUrlTemplate_ = langUrlTemplate;

    /**
     * @type {ngeo.statemanager.Location}
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * @type {function(Date, string, string=) : string}
     * @private
     */
    this.dateFormat_ = $filter('date');

    /**
     * Hash containing the result of feature support tests.
     * @type {Object}
     * @export
     */
    this.has = {};

    try {
      this.has['localStorage'] = !!window.localStorage.getItem;
    } catch (exception) {
      this.has['localStorage'] = false;
    }

    /**
     * @type {string}
     * @export
     */
    this.land = this.getLandName_();

    /**
     * @type {string}
     * @export
     */
    this.domain_name = LAND_TO_DOMAIN_NAME[this.land];

    /**
     * @type {string|undefined}
     * @export
     */
    this.lang = undefined;

    /**
     * @type {string}
     * @export
     */
    this.season = 'summer';

    /**
     * @type {string|undefined}
     * @export
     */
    this.embedded = ngeoLocation.getParam('embed');

    /**
     * @type {boolean}
     * @export
     */
    this.routeLogoState = ngeoLocation.hasParam('logo') ? ngeoLocation.getParam('logo') === 'yes' : true;

    this.scope_.$watch(() => this.routeLogoState, () => {
      this.manageRouteLogo_();
    });

    this.detourState = ngeoLocation.hasParam('detours') ? ngeoLocation.getParam('detours') === 'yes' : true;

    this.scope_.$watch(() => this.detourState, () => {
      this.ngeoLocation_.updateParams({
        'detours': this.detourState ? 'yes' : 'no'
      });
      this.updateDetours();
    });

    this.scope_.$watch(() => this.season, (season) => {
      console.assert(season === 'summer' || season === 'winter');
      this.ngeoLocation_.updateParams({
        'season': season
      });
    });

    /**
     * @type {string}
     */
    this.menuInitialState_ = 'show';

    this.scope_.$on(MAIN_MENU_EVENT_NAME.INIT_COLLAPSED, () => {
      // allow to keep the menu hidden in certain cases when initializing the
      //  application, for example when loading weather permalink
      this.menuInitialState_ = 'hidden';
    });
    this.scope_.$on(MAIN_MENU_EVENT_NAME.EXPAND, () => {
      if (smallScreen()) {
        // On small screens, the menu must be collapsed to enable querying.
        appQueryStateService.addQueryBlocker('mobileMenuOpen');
      }
    });
    this.scope_.$on(MAIN_MENU_EVENT_NAME.COLLAPSE, () => {
      if (smallScreen()) {
        appQueryStateService.removeQueryBlocker('mobileMenuOpen');
      }
    });

    /**
     * @type {boolean}
     */
    this.trackImportError = false;

    this.scope_.$on('trackImportError', () => {
      this.trackImportError = true;
    });

    this.scope_.$on('loginChange', (event, loggedIn) => {

      if (loggedIn) {
        if (ngeoLocation.getParam('showDialog') === 'importcoordinates') {
          ngeoLocation.deleteParam('showDialog');
        }
        ngeoLocation.deleteParam('showLogin');
      }

      let state = 'user';

      const showLogin = ngeoLocation.hasParam('showLogin');
      const hasTrackId = ngeoLocation.hasParam('trackId') || ngeoLocation.hasParam('recordedTrackId');
      const coordinates = ngeoLocation.hasParam('coordinates');
      const validTrackParam = !this.trackImportError && (hasTrackId || coordinates);
      if (showLogin || loggedIn || validTrackParam) {
        this.showMenu('mytracks', true);
      } else if (!smallScreen()) {
        // Display the main menu if it's not a small screen
        this.showMenu('');
      }
      if (validTrackParam) {
        state = 'track-facts';
      }
      this.routingState_.go(state);
    });

    this.scope_.$on('logout', () => {
      this.routingState_.go('user');
    });

    /**
     * @type {!Object.<string, ol.layer.Tile|ol.layer.Vector>}
     * @private
     */
    this.layers_ = {};

    this.enabledLayers_ = [];

    const projectLanguages = ['en', 'de', 'fr', 'it'];
    let lang = ngeoLocation.getParam('lang');
    if (!projectLanguages.includes(lang)) {
      lang = getBrowserLanguage(projectLanguages) || 'en';
    }
    console.assert(lang);
    this.switchLanguage(lang);

    const extent = MAP_PROJECTION.getExtent();

    this.zoom = new olControlZoom({
      zoomInLabel: '',
      zoomOutLabel: '',
      zoomInTipLabel: '',
      zoomOutTipLabel: ''
    });

    // required for correct handling of resolution change
    this.zoomButtonClicked = false;

    /**
     * @type {!ol.Map}
     * @export
     */
    this.map = new olMap({
      controls: [
        new olControlScaleLine(),
        this.zoom
      ],
      interactions: olInteractionDefaults({
        altShiftDragRotate: false,
        keyboard: false,
        pinchRotate: false
      }),
      view: new olView({
        center: getCenter(extent),
        enableRotation: false,
        projection: MAP_PROJECTION,
        constrainResolution: false,
        resolution: 500,
        resolutions: resolutions
      }),
      maxTilesLoading: 128,
    });

    appMapService.map = this.map;

    const crosshair = ngeoLocation.getParam('crosshair');
    if (crosshair === 'marker') {
      this.map.once('postrender', () => {
        ngeoLocation.deleteParam('crosshair');
        const geom = new olPoint(this.map.getView().getCenter());
        const feature = new olFeature(geom);
        appHighlightFeature.addFeature(feature, 'permalink_crosshair', {
          'kind': 'coordinates',
          'isProtected': true,
        });
      });
    }

    /**
     * @type {app.HighlightFeature}
     * @private
     */
    this.appHighlight_ = appHighlightFeature;

    /**
     * @type {app.GeolocationService}
     * @private
     */
    this.geolocationService_ = appGeolocationService;

    /**
     * @type {string}
     * @export
     */
    this.href = ngeoLocation.getUriString();

    this.scope_.$on('ngeoLocationChange', () => {
      this.href = this.ngeoLocation_.getUriString();
    });

    /**
     * The url to open the full map in embedded mode
     * @type {string}
     * @export
     */
    this.embeddedFullUrl = appEmbedService.getEmbeddedFullUrl();

    /**
     * @type {ol.control.Control}
     * @export
     */
    this.mousePosition = new olControlMousePosition(/** @type {olx.control.MousePositionOptions} */({
      projection: proj2056,
      className: 'mouse-position',
      coordinateFormat,
      undefinedHTML: '&nbsp;'
    }));

    this.setupAdminLayers_();

    this.parseDirectLinks_();

    /**
     * @type {!{layers: Object<string, boolean>, groups: Object<string, boolean>}}
     * @export
     */
    this.wms = {
      layers: {},
      groups: {}
    };
    this.setupWMSLayer_();

    this.setSeasonFromParams_();

    /**
     * @type {import('ol/layer/VectorTile.js').default[]}
     */
    this.routeLayers = [];

    let zindexFix = 0.5; // easily ensure the layer order is what we think
    for (const land of ORDERED_LANDS) {
      const layer = LAND_TO_LAYER[land];
      // updating the filteredWmsLayers is done in the watcher
      const filteredWmsLayers = this.filterLayersByLand_(this.wms.layers, layer);
      const routeSource = createRouteSource(this.apiUrl_, land);
      const mvtLayer = createRouteLayer(routeSource, filteredWmsLayers, layer, land);
      mvtLayer.setZIndex(mvtLayer.getZIndex() + zindexFix);
      zindexFix -= 0.01;
      const shown = Object.keys(filteredWmsLayers).length > 0;
      mvtLayer.setVisible(shown);
      this.routeLayers.push(mvtLayer);
    }
    this.routeLayers.forEach(l => this.map.addLayer(l));

    /**
     * @type {boolean}
     * @export
     */
    this.photoLayerState = ngeoLocation.hasParam('photos') ? ngeoLocation.getParam('photos') === 'yes' : true;
    this.ngeoLocation_.updateParams({'photos': this.photoLayerState ? 'yes' : 'no'});
    this.appPhotoService_.initialize(this.map, this.wms.layers, this.season);
    this.scope_.$watch(() => this.photoLayerState, (state) => {
      this.appPhotoService_.setVisible(state);
      this.ngeoLocation_.updateParams({'photos': state ? 'yes' : 'no'});
    });

    // hide the main menu
    // The user controller will show the wanted menu later
    this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.COLLAPSE);

    this.appHighlight_.init(this.map, this.scope_, appCacheBustService);

    this.geolocationService_.init(this.map);

    this.handleCenterOnUserParam_();

    this.mobileInteractionService_ = appMobileInteractionService;
    this.mobileInteractionService_.enableHideMenuOnMobile();

    /**
     * @private
     */
    this.cacheBustService_ = appCacheBustService;

    /**
     * @type {ol.EventsKey}
     * @private
     */
    this.changeCursorListenerKey_ = listen(this.map, 'pointermove', (event) => {
      if (!event.dragging) {
        const pixel = this.map.getEventPixel(event.originalEvent);
        const hit = this.map.hasFeatureAtPixel(pixel, (layer) => {
          return !!layer && layer.get('type') === 'selected_tracks';
        });
        this.map.getTargetElement().style.cursor = hit ? 'pointer' : '';
      }
    });

    /**
     * @type {boolean}
     * @export
     */
    this.queryActive = true;

    this.setupEmbeddedMode_();

    this.cesium.initialize(this.map, this.scope_, this.has, this.wms);

    this.menuInitialState_ = 'show';

    let permalinkLayers = [];
    this.scope_.$on('layersUpdated', (event, layers) => {
      const added = layers.filter(layer => !permalinkLayers.includes(layer));
      added.forEach(layer => {
        gtag('event', 'layers', {
          'event_label': layer
        });
      });
      permalinkLayers = layers;
    });

    /**
     * @const
     * Is set in constructor
     * @type {Cesium.Rectangle}
     */
    this.CESIUM_RECTANGLE;

    this.handleZoomBtn_();

    if (this.has['localStorage']) {
      this.hideMountaineeringDisclaimer = window.localStorage.getItem('hideMountaineeringDisclaimer') === 'true';

      this.scope_.$watch(() => this.hideMountaineeringDisclaimer, (value) => {
        if (value !== undefined) {
          if (value) {
            window.localStorage.setItem('hideMountaineeringDisclaimer', 'true');
          } else {
            window.localStorage.removeItem('hideMountaineeringDisclaimer');
          }
        }
      });

    }

    this.enabledMountaineeringLayers = [];
  }

  /**
   * @export
   * @return {string}
   */
  routingStateName() {
    return this.routingState_.current.name;
  }

  /**
   * @export
   * @return {boolean}
   */
  isWaiting() {
    return this.appWaiting_.isWaiting();
  }

  /**
   * @private
   * @return {string}
   */
  getLandName_() {
    let land = this.ngeoLocation_.getParam('land');
    // land name can be in the short (kanu) or complete form (kanuland)
    if (land) {
      land = LAND_ALIASES[land] || land;
    }
    if (!LANDS.includes(/** @type {string} */ (land))) {
      // use the server host
      const host = window.location.host;
      land = HOST_TO_LAND[host] || 'schweizmobil';
    }
    console.assert(land);
    return land;
  }

  /**
   * @param {string} a Layer name
   * @param {string} b Layer name
   * @return {number}
   */
  sortLayerByName(a, b) {
    return layerIndex(b) - layerIndex(a);
  }

  /**
   * @return {Object.<string, boolean>}
   */
  activeLayers() {
    return objectFilter(this.wms.layers, value => value);
  }

  /**
   * see https://project.camptocamp.com/wikis/veloland_schweizaktiv/Alias
   * @private
   */
  parseDirectLinks_() {
    const ngeoLocation = this.ngeoLocation_;
    let layers = ngeoLocation.getParam('layers');
    layers = layers ? layers.split(',') : [];
    if (ngeoLocation.hasParam('accomodation') || ngeoLocation.hasParam('accommodation')) {
      // Having the 'accomodation' parameter is equivalent to having the
      // 'Accomodation' keyword in 'layers' parameter.
      layers.push('Accomodation');
    }

    const queryParams = {};

    const links = DIRECT_LINKS;
    for (const key in links) {
      const aliases = links[key];
      for (let i = 0, ii = aliases.length; i < ii; i++) {
        const match = ngeoLocation.getParam(aliases[i]);
        if (match) {
          // activate the layer
          layers.push(key);
          // prepare the query params
          if (match !== 'all') {
            queryParams[key] = match;
          }
        }
      }
    }

    // special cases
    if (ngeoLocation.getParam('kanton') === 'all') {
      this.adminLayers_['kanton'].setVisible(true);
    }
    if (ngeoLocation.getParam('gemeinde') === 'all') {
      this.adminLayers_['gemeinde'].setVisible(true);
    }
    const onlyRoute = ngeoLocation.getParam('onlyRoute');
    const route = ngeoLocation.getParam('route');
    const etappe = ngeoLocation.getParam('etappe');
    if (route || etappe) {
      const layerGroup = LAND_TO_LAYER[this.land];
      console.assert(layerGroup);
      if (onlyRoute === undefined) {
        layers.push(layerGroup);
      }

      // recenter params
      const pkey = this.land === 'mtb' ? 'Mtbland' : layerGroup;
      if (route && route !== 'all') {
        this.getRecenterParams_(pkey + 'Routen', route, queryParams);
      }
      if (etappe && etappe !== 'all') {
        this.getRecenterParams_(pkey + 'Etappen', etappe, queryParams);
      }
    }
    // detour
    const detour = ngeoLocation.getParam('detour');
    if (detour) {
      queryParams['GoodToKnowDetour'] = detour;
      queryParams['attributes'] = 1;
      this.detourState = true;
    }

    // parse the highlight param
    const highlight = ngeoLocation.getParam('highlight');
    if (highlight) {
      const highlightPairs = highlight.split(',');
      for (let j = 0, jj = highlightPairs.length; j < jj; j++) {
        const highlightValue = highlightPairs[j].split('*');
        console.assert(highlightValue.length === 2);
        layers.push(highlightValue[0]);
        queryParams[highlightValue[0]] = highlightValue[1].replace(/-/g, ',');
      }
    }

    ngeoLocation.updateParams({
      'layers': layers.join(',')
    });

    const url = this.apiUrl_ + '4/query/featuresmultilayers';
    const queryUrl = appendParams(url, queryParams);

    if (!isEmpty(queryParams)) {
      const hasXYParam = ngeoLocation.hasParam('X') && ngeoLocation.hasParam('Y');

      const response = this.http_.get(queryUrl);
      response.then((response) => {
        const format = new olFormatGeoJSON();
        const features = format.readFeatures(response.data);

        // only recenter if 'X'/'Y' are not in the permalink
        if (!hasXYParam) {
          const view = this.map.getView();
          let extents = features.map((feature) => {
            const geometry = feature.getGeometry();
            if (!geometry) {
              const extent = createEmpty();
              const start = /** @type {ol.Coordinate|undefined} */ (feature.get('coordinate_start'));
              const end = /** @type {ol.Coordinate|undefined} */ (feature.get('coordinate_end'));
              console.assert(start || end);
              if (start) {
                extendCoordinate(extent, start);
              }
              if (end) {
                extendCoordinate(extent, end);
              }
              return extent;
            } else {
              return feature.getGeometry().getExtent();
            }
          });
          // custom detour
          if (detour) {
            const points = [];
            const extentBuffer = 500;
            objectForEach(features, feature => {
              // add possible points to recentering extents
              const items = ['coordinate_start', 'coordinate_end', 'coordinate_intermediate', 'closed_way_line'];
              for (let i = 0, l = items.length; i <= l; i++) {
                const val = /** @type {Array} */ (feature.get(items[i]));
                if (val && val.length > 0) {
                  if (Array.isArray(val[0])) {
                    val.forEach(point => points.push(new olGeomCircle(point, extentBuffer).getExtent()));
                  } else {
                    points.push(new olGeomCircle(val, extentBuffer).getExtent());
                  }
                }
              }
              // enable related lands layers
              const lands = feature.get('land').split(',');
              const layers = lands.map((land) => LAYER_REPLACEMENT[land]);
              if (feature.get('hiking_network') && !layers.includes('Wanderland')) {
                layers.push('Wanderland');
              }
              for (const layer of layers) {
                if (layer) {
                  if (LAYERGROUPS.includes(layer)) {
                    this.wms.groups[layer] = true;
                  } else {
                    this.wms.layers[layer] = true;
                  }
                }
              }
              this.setSeasonFromLayers_(layers);
            });
            extents = extents.concat(points);
          }
          if (extents.length > 0) {
            const extent = extentMerge(extents);
            if (getArea(extent) === 0) {
              view.setCenter(getCenter(extent));
              view.setResolution(5);
            } else {
              const size = this.map.getSize();
              console.assert(size);
              let fitOptions;
              if (size) {
                fitOptions = /** @type{olx.view.FitOptions}*/({size});
              }
              view.fit(extent, fitOptions);
            }
          }
        }

        const tipp = features.some(function(feature) {
          return feature.get('layer').includes('Tipp');
        });

        if (tipp) {
          this.wms.layers['VelolandEtappenTipp'] = true;
        }

        // highlight the features
        features.forEach((feature) => {
          const layer = feature.get('layer').toLowerCase();
          if (!layer.includes('slowup')) {
            let id = layer + '#' + feature.getId();
            if ((feature.get('r_number') || feature.get('e_number')) !== undefined) {
              const layerName = feature.get('layer')
                .replace('Routen', 'Etappen')
                .replace('Local', 'Lokal');
              feature.set('layer', layerName);
              if (feature.get('r_number') !== undefined) {
                const routeNumber = parseInt(feature.get('r_number'), 10);
                const routeName = feature.get('layer');
                // this id structure is important to link the highlight with the
                // route tooltips UI (show/hide route, show/hide etappen)
                id = routeName + routeNumber + '_tooltip';
              }
              if (feature.get('e_number') !== undefined) {
                // etappen id is different from routen for godknowwhat reason
                id = 'etappen#' + feature.getId();
              }
              feature.set('isProtected', true);
            }
            if (layer == 'detour') {
              // special detour
              const id = /** @type {number} */ (feature.getId());
              this.appDetourService_.highlightFeatures(id);
            } else {
              let kind;
              if (feature.getGeometry().getType() === 'Point') {
                // poi
                kind = layer;
                if (layer.includes('taxi')) {
                  kind = 'taxi';
                }
              } else {
                // route or segment
                // with `onlyRoute` the network layer is not visible; the land
                // color must be displayed.
                kind = onlyRoute !== undefined ? this.land : 'transparent';
                if (layer.includes('gemeinde') || layer.includes('kanton') || layer.includes('swisspark')) {
                  kind = layer;
                }
              }
              this.appHighlight_.addFeature(feature, id, {
                'kind': kind
              });
            }
          }
        });
      });
    }
  }

  /**
   * @private
   */
  setSeasonFromParams_() {
    const ngeoLocation = this.ngeoLocation_;
    const season = ngeoLocation.getParam('season');
    if (season && (season == 'winter' || season == 'summer')) {
      this.season = season;
    } else {
      const layers = ngeoLocation.getParam('layers');
      this.setSeasonFromLayers_(layers ? layers.split(',') : []);
    }
  }

  /**
   * @param {Array<string>} layers Layers array.
   * @private
   */
  setSeasonFromLayers_(layers) {
    const hasWinterLayer = layers
      .filter(layer => !!layer)
      .some((layer) => isWinterLayer(layer.toLowerCase()));
    this.season = hasWinterLayer ? 'winter' : 'summer';
  }

  /**
   * @private
   */
  handleCenterOnUserParam_() {
    if (this.ngeoLocation_.hasParam('centerOnUser')) {
      this.geolocationService_.trackOnce();
      this.ngeoLocation_.deleteParam('centerOnUser');
    }
  }

  /**
   * @param {string} prefix query prefix.
   * @param {string} id query value.
   * @param {Object.<string, string>} params destination object.
   * @private
   */
  getRecenterParams_(prefix, id, params) {
    if (id !== undefined) {
      const types = LAND_TO_LAYER_TYPE[this.land];
      console.assert(types);
      for (let i = 0, ii = types.length; i < ii; i++) {
        params[prefix + types[i]] = id;
      }
    }
  }

  /**
   * @param {string} lang Language code.
   * @export
   */
  switchLanguage(lang) {
    this.gettextCatalog.setCurrentLanguage(lang);
    this.gettextCatalog.loadRemote(this.langUrlTemplate_.replace('__lang__', lang));
    this.lang = lang;
    this.ngeoLocation_.updateParams({'lang': lang});
  }

  /**
   * @export
   */
  sendLinkByEmail() {
    window.location = 'mailto:?body=' +
        encodeURIComponent(this.ngeoLocation_.getUriString());
  }

  /**
   * @param {string} target The class name to click (without leading dot).
   * @param {boolean=} opt_immediately show the targeted menu without animations
   * @export
   */
  showMenu(target, opt_immediately) {
    // if initial state is set to hidden, do nothing
    if (this.menuInitialState_ == 'hidden') {
      return;
    }

    if (target.length <= 0) {
      //if target is invalid, display main menu then return
      this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.EXPAND);
      return;
    }

    if (opt_immediately) {
      this.showMenuImmediately_(target);
      return;
    }

    if ($('.menu').is(':visible')) {
      // the menu is visible
      const visibleMenuItem = visibleSubMenu();
      if (visibleMenuItem) {
        // a sub menu is visible, hide it if it's not the target menu
        if (!visibleMenuItem.hasClass(target)) {
          // back to the main menu
          this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.COLLAPSE_SUB_MENU, {showMainMenu: false});
        } else {
          // the menu is already visible, nothing to do
          return;
        }
      }
      this.scope_.$emit(MENU_TOGGLE_COLLAPSE, target);
    } else {
      // the full menu is not visible, show it
      this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.EXPAND);
      const deregister = this.scope_.$on(MAIN_MENU_EVENT_NAME.MAIN_MENU_SHOWN, (event) => {
        this.scope_.$emit(MENU_TOGGLE_COLLAPSE, target);
        deregister();
      });
    }
  }

  /**
   * @param {string} target The class name to click (without leading dot).
   * @private
   */
  showMenuImmediately_(target) {
    const menu = $('#menu-container .menu');
    const targetMenu = menu.find('.menu-item.' + target);

    //Hide then close all submenus
    menu.find('.menu-item').addClass('hidden');
    menu.find('[app-collapse-menu]').addClass('collapsed');
    menu.find('.collapse').removeClass('in');

    // Wait for DOM to load in targetMenu
    // This is required such that the tracks menu is only expanded once
    // the tracks component has been loaded.
    onRendered(targetMenu, () => {
      //Show targeted menu
      targetMenu.find('.collapse').addClass('in');
      targetMenu.find('[app-collapse-menu]').removeClass('collapsed');
      targetMenu.removeClass('hidden');
      this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.SUB_MENU_LOADED, target);
    });

    //Show the main menu
    this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.EXPAND, {no_animation: true});

    //Show the hamburger button
    $('.menu-button-collapse-sub-menu').removeClass('hidden');

    //Hide menu expand button
    $('.main-menu-expand').addClass('hidden');
  }

  /**
   * @private
   */
  setupAdminLayers_() {
    this.adminLayers_ = {
      'land': new olLayerTile({
        minResolution: 250,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.swissboundaries3d-land-flaeche.fill', 'current', 'png'
        ),
        zIndex: Z_INDEX.ADMIN,
      }),
      'kanton': new olLayerTile({
        visible: false,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.swissboundaries3d-kanton-flaeche.fill', 'current', 'png'
        ),
        zIndex: Z_INDEX.ADMIN,
      }),
      'gemeinde': new olLayerTile({
        visible: false,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.swissboundaries3d-gemeinde-flaeche.fill', 'current', 'png'
        ),
        zIndex: Z_INDEX.ADMIN,
      })
    };

    Object.keys(this.adminLayers_).forEach((key) => {
      this.map.addLayer(this.adminLayers_[key]);
    });
  }

  /**
   * @private
   */
  setupEmbeddedMode_() {
    if (this.embedded !== undefined) {
      document.body.classList.add('embedded');
      if (this.embedded !== 'simple') {
        document.body.classList.add('embedded-banner');
      }

      // deactivate query
      this.queryActive = false;

      // don't change cursor to 'pointer' on pointer move
      unlistenByKey(this.changeCursorListenerKey_);

      if (this.ngeoLocation_.getParam('noNavigation') !== undefined) {
        document.body.classList.add('embedded-no-navigation');
        // deactivate all interactions
        this.map.getInteractions().forEach(function(interaction) {
          interaction.setActive(false);
        });
      }
    }
  }

  /**
   * @private
   */
  getLayers_() {
    if (isEmpty(this.layers_)) {
      const sourceConfig = this.sourceConfig.wmts.chmobil;
      const wmtsUrls = this.wmtsUrls_;
      const layers = this.layers_;
      const poiLayersDataUrl = this.poiLayersDataUrl_;
      sourceConfig.layers.forEach(
        /**
         * @param {{
         *   name: string,
         *   time: string,
         *   layername: (string|undefined),
         *   parent: (boolean|undefined),
         *   cesiumIgnore: (boolean|undefined)
         * }} val
         */
        (val) => {
          // group layers
          let cesiumIgnore = !WMTS_GROUPS.includes(val.name);
          cesiumIgnore = val.cesiumIgnore !== undefined ? val.cesiumIgnore : cesiumIgnore;
          const layername = val.layername || val.name;
          let zIndex = Z_INDEX.ROUTE;
          if (layername === 'Slowup') {
            zIndex = Z_INDEX.SLOWUP;
          }
          if (!VECTOR_TILE_LAYERS.includes(val.name)) {
            layers[val.name] = new olLayerTile({
              'parent': val.parent !== undefined ? val.parent : true,
              'urls': wmtsUrls,
              'layername': layername,
              'time': val.time,
              'cesiumIgnore': cesiumIgnore,
              useInterimTilesOnError: false,
              visible: false,
              source: schweizmobil(wmtsUrls, layername, val.time),
              zIndex: zIndex,
            });
          } else {
            layers[val.name] = this.createVectorTileLayer(val.name);
          }
          // children layers
          const children = LAYER_TREE[val.name];
          if (children) {
            const format = new olFormatGeoJSON({
              dataProjection: MAP_PROJECTION
            });

            children.forEach(name => {
              if (VECTOR_LAYERS.includes(name)) {
                layers[name] = new olLayerVector({
                  visible: false,
                  style: (feature, resolution) => {
                    if (resolution < feature.get('resolution')) {
                      const key = /** @type {string} */ (feature.get('subtype')) || name;
                      return poiStyle(key, resolution, this.cacheBustService_);
                    } else {
                      return null;
                    }
                  },
                  source: new olSourceVector({
                    url: `${poiLayersDataUrl}21781/clustered_${name}.geojson`,
                    format: format
                  }),
                  zIndex: Z_INDEX.UP,
                });
              } else {
                layers[name] = new olLayerTile({
                  'urls': wmtsUrls,
                  'layername': name,
                  'time': val.time,
                  'cesiumIgnore': cesiumIgnore,
                  useInterimTilesOnError: false,
                  visible: false,
                  source: schweizmobil(wmtsUrls, name, val.time),
                  zIndex: Z_INDEX.ROUTE,
                });
              }
            });
          }
        }
      );
      // add other swisstopo layers
      this.layers_['Slope'] = new olLayerTile({
        visible: false,
        opacity: 0.4,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.hangneigung-ueber_30', 'current', 'png'
        ),
        zIndex: Z_INDEX.OVERLAY,
      });
      this.layers_['WildlifeArea'] = new olLayerTile({
        visible: false,
        opacity: 0.6,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.bafu.wrz-wildruhezonen_portal', 'current', 'png'
        ),
        zIndex: Z_INDEX.OVERLAY,
      });
      this.layers_['WildlifeReserve'] = new olLayerTile({
        visible: false,
        opacity: 0.6,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.bafu.wrz-jagdbanngebiete_select', 'current', 'png'
        ),
        zIndex: Z_INDEX.OVERLAY,
      });
      this.layers_['AlpguardDog'] = new olLayerTile({
        visible: false,
        opacity: 0.5,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.bafu.alpweiden-herdenschutzhunde', 'current', 'png'
        ),
        zIndex: Z_INDEX.OVERLAY,
      });

      this.layers_['Wanderwegnetz'] = new olLayerTile({
        visible: false,
        opacity: 0.7,
        maxResolution: 50,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.swisstlm3d-wanderwege', 'current', 'png'
        ),
        zIndex: Z_INDEX.HIKING_TRAILS,
      });

      const sorted_layers = Object.keys(this.layers_).sort(MainController.sortLayerByName);
      sorted_layers.forEach((key) => {
        if (!MVT_LAYERS.includes(key)) {
          this.map.addLayer(this.layers_[key]);
        }
      });
    }
    return this.layers_;
  }

  /**
   * @private
   * @return {Array.<string>}
   */
  getLayersFromParams_() {
    let layers = this.ngeoLocation_.getParam('layers');
    layers = layers ? layers.split(',') : [];

    // Handle aliases
    const replaceAliases = function(value) {
      const lowerValue = value.toLowerCase();
      const replacement = LAYER_REPLACEMENT[lowerValue];
      if (replacement) {
        return replacement;
      }
      if (lowerValue.includes('routen')) {
        return value.replace('Routen', 'Etappen');
      }

      return value;
    };
    layers = layers.map(replaceAliases);

    this.slowup = layers.includes('Slowup');
    if (this.slowup) {
      gtag('event', 'slowup');
    }

    // wanderwegnetz special case, 'wanderwegnetz' is assumed to be the only
    // possible value for this key.
    const layer = this.ngeoLocation_.getParam('layer');
    if (layer) {
      console.assert(layer === 'wanderwegnetz');
      layers.push('Wanderwegnetz');
      this.ngeoLocation_.deleteParam('layer');
    }

    // remove duplicates
    layers = layers.filter(function(name, index, arr) {
      return arr.indexOf(name) === index;
    });

    this.ngeoLocation_.updateParams({
      'layers': layers.join(',')
    });

    return layers;
  }


  /**
   * @private
   */
  setupWMSLayer_() {
    const layers = this.getLayersFromParams_();

    /** @type {!Object.<string, boolean>} */
    const lselection = {};

    /** @type {!Object.<string, boolean>} */
    const gselection = {};

    if (layers.length > 0) {
      layers.forEach(function(layer) {
        if (LAYERGROUPS.includes(layer)) {
          gselection[layer] = true;
        } else {
          lselection[layer] = true;
        }
      });
    }

    this.wms.groups = gselection;
    this.wms.layers = lselection;

    // Handle indeterminate state initialization
    this.timeout_(function() {
      // Get all the top menu checkboxes
      const topBoxes = $('input[type="checkbox"][app-checkbox~=".menu-item-body"]');
      topBoxes.each(/** @this {!Object} */ function() {
        const checkbox = $(this);
        // Similar to the checkbox directive
        const container = $(checkbox.attr('app-checkbox'));
        const children = container.find('[type=checkbox]');
        const countChecked = children.filter(':checked').length;
        const state = countChecked !== 0 && countChecked !== children.length;
        checkbox.prop('indeterminate', state);
      });
    }, 20); // some timer to let angular propagate wms.layers values to the UI


    this.scope_.$watch(() => {
      return this.wms.layers;
    }, async (value) => {

      // update styling on mvt layers
      for (const routeLayer of this.routeLayers) {
        const layerName = routeLayer.get('name');
        const filteredWmsLayers = this.filterLayersByLand_(this.wms.layers, layerName);
        const shown = Object.keys(filteredWmsLayers).length > 0;
        if (routeLayer.getVisible() !== shown) {
          routeLayer.setVisible(shown);
        }
        updateRouteLayerStyle(routeLayer, filteredWmsLayers);
      }

      const layers = [];
      for (const key in value) {
        if (value[key]) {
          layers.push(key);
        }
      }
      const permalink_layers = layers.slice();

      const wmts_layers = this.getLayers_();
      for (const group in LAYER_TREE) {
        const children = LAYER_TREE[group];

        const allEnabled = children.every(name => layers.includes(name));
        if (allEnabled) {
          children.forEach(name => {
            olArrayRemove(permalink_layers, name);
          });
          permalink_layers.push(group);
        }
      }
      // change children layers visibility
      objectForEach(wmts_layers, function(layer, name) {
        if (!layer.get('parent')) {
          const visible = layers.includes(name);
          layer.setVisible(visible);
        }
      });

      this.updateLayersParam_(permalink_layers);

      this.enabledLayers_ = layers;

      // Change detours
      await this.updateDetours();

      const mountaineeringEnabled = MOUNTAINEERING_LAYERS.some(l => this.wms.layers[l]);
      if (mountaineeringEnabled &&
          this.enabledLayers_.length > this.enabledMountaineeringLayers.length &&
          !this.hideMountaineeringDisclaimer &&
          this.embedded == undefined) {
        $('#MountaineeringDisclaimer').modal('show');
      }
      this.enabledMountaineeringLayers = this.enabledLayers_;

      this.manageRouteLogo_();
      this.appPhotoService_.updateActiveLands(this.wms.layers, this.season);

    }, true);
  }

  /**
   * @param {Array.<string>} layers List of activated layers.
   * @private
   */
  updateLayersParam_(layers) {
    this.scope_.$root.$broadcast('layersUpdated', layers);
    if (layers.length > 0) {
      this.ngeoLocation_.updateParams({'layers': layers.join(',')});
    } else {
      this.ngeoLocation_.deleteParam('layers');
    }
  }

  /**
   * @export
   * @return {string}
   */
  getOtherSeason() {
    return this.season === 'winter' ?
      this.gettextCatalog.getString('Summer') :
      this.gettextCatalog.getString('Winter');
  }

  /**
   * @private
   */
  manageRouteLogo_() {
    this.ngeoLocation_.updateParams({
      'logo': this.routeLogoState ? 'yes' : 'no'
    });
    const routeLogos = this.appRouteLogoService_.getRouteLogoLayers(this.wms.layers);
    objectForEach(routeLogos, (layer, land) => {
      let visible = false;
      for (const key in this.wms.layers) {
        if (this.wms.layers[key] && key.startsWith(land) && this.routeLogoState) {
          visible = true;
          break;
        }
      }
      if (visible) {
        layer.getSource().changed();
      }
      layer.setVisible(visible);
    });
  }

  /**
   * @export
   */
  toggleSeason() {
    const newSeason = this.season === 'winter' ? 'summer' : 'winter';
    this.season = newSeason;
    // removing the highlight parameter to prevent the creation of inconsistent urls.
    this.ngeoLocation_.deleteParam('highlight');
  }

  /**
   * @export
   */
  loggedIn() {
    return this.authentication_.loggedIn;
  }

  /**
   * @export
   */
  showLogin() {
    this.showMenu('mytracks');
    this.routingState_.go('user');
  }

  /**
   * @private
   */
  handleZoomBtn_() {
    this.zoom.element.addEventListener('click', () => this.zoomButtonClicked = true);
    // handle zoom button click in 2D mode
    this.map.getView().on('change:resolution', evt => {
      if (!this.zoomButtonClicked) {
        return;
      }
      this.zoomButtonClicked = false;
      const view = this.map.getView();
      const cur = view.getResolution();
      const step = evt.oldValue > cur ? -1 : 1;
      // compute the next resolution
      const next = getConstrainedResolution(view, cur + step, step);
      view.setResolution(next);
    });
  }

  /**
   * @param {Object<string, boolean>} selection
   * @param {string} land
  */
  filterLayersByLand_(selection, land) {
    const filteredWmsLayers = {};
    for (const layer in selection) {
      if (!selection[layer]) {
        continue;
      }
      const layerGroup = LAND_LAYER_TO_PSEUDO_GROUP_LAYER[layer];
      if (layerGroup === land) {
        filteredWmsLayers[layer] = true;
      }
    }
    return filteredWmsLayers;
  }

  createVectorTileLayer(name) {
    switch (name) {
      case 'Slowup':
        const slowupSource = createSlowupVectorSource(this.apiUrl_);
        return createSlowupVectorLayer(slowupSource, this.cacheBustService_);
      case 'Swisspark':
        const swissparkSource = createSwissparkVectorSource(this.apiUrl_);
        return createSwissparkVectorLayer(swissparkSource);
      case 'CrosscountryNetwork':
        return new CrosscountryNetworkLayer({
          baseDataUrl: this.baseDataUrl_,
          visible: false,
          opacity: 0.7,
          maxResolution: 50,
          zIndex: Z_INDEX.HIKING_TRAILS,
        });
      default:
        console.assert(!VECTOR_TILE_LAYERS.includes(name));
        return undefined;
    }
  }

  async updateDetours() {
    try {
      const detours = await this.appDetourService_.getDetoursLayers();
      objectForEach(detours, (layer, landAndKind) => {
        let detourVisible = false;
        if (this.detourState) {
          for (const subLayerName in this.wms.layers) {
            const land = landAndKind.split('_')[0]; // ex Wanderland_points
            const subLayerActive = this.wms.layers[subLayerName];
            let subLayerOfThisLand = subLayerName.startsWith(land);
            subLayerOfThisLand |= land === "Wanderland" && subLayerName.toLowerCase() == 'wanderwegnetz';
            if (subLayerActive && subLayerOfThisLand) {
              detourVisible = true;
              break;
            }
          }

          if (detourVisible) {
            layer.getSource().changed();
          }
        }
        layer.setVisible(detourVisible);
      });
    } catch (err) {
      console.error(err);
    } finally {
      if (this.cesium.olcsEnabled) {
        // We are in 3D
        // To ensure the detours are fetched we unhide the layergroup and force a rendering
        const layerGroup = this.map.getLayerGroup();
        layerGroup.setVisible(true);
        this.map.renderSync();
        layerGroup.setVisible(false);
      }
    }
  }
}

appBase.controller('MainController', MainController);
